'use client';
import type { LayoutData } from '@mnd-apps/public/lib/menus';
import { useMediaQuery } from '@mnd-frontend/hooks';
import MynewsdeskLogo from '@mnd-frontend/ui/public/components/Logo/MynewsdeskLogo';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import type { CurrentLocaleDataType } from '@mnd-frontend/ui/public/types/locale';
import type { PrimaryNavItemType } from '@mnd-frontend/ui/public/types/menu';
import {
  BREAKPOINTS,
  SIZING_7,
  SPACING_0_5,
  SPACING_2,
  SPACING_3,
} from '@mnd-frontend/ui/shared/tokens';
import { throttle } from 'lodash-es';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const HeaderNavMobile = dynamic(() => import('./HeaderNavMobile'));
const HeaderNavDesktop = dynamic(() => import('./HeaderNavDesktop'));

const StyledHeader = styled.header<{
  $visible?: boolean;
  $scrolling?: boolean;
}>`
  position: sticky;
  height: ${SIZING_7};
  padding: ${SPACING_0_5} 0 ${SPACING_0_5} ${SPACING_2};
  width: 100%;
  z-index: 99;
  top: 0;
  background: #ffffff;
  transform: translateY(-100%);
  transition: transform 0.5s ease;
  backface-visibility: hidden;
  will-change: transform;

   ${({ $visible, $scrolling }) => {
     if ($visible) {
       return css`
          background: #ffffff;
          transform: translateY(0);
          position: fixed;
        `;
     }
     if ($scrolling) {
       return css`
          position: fixed;
        `;
     }

     return css`
        transform: translateY(-100%);
      `;
   }}

  .content-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  
  @media (min-width: ${BREAKPOINTS.XL}px) {
    background: #ffffff;
    align-items: center;
    height: auto;
    padding: ${SPACING_2} 0;

    .content-wrapper {
      justify-content: flex-start;
      gap: ${SPACING_3};
    }

    // Blurred background when menu is open
    &:after {
      content: "";
      position: fixed;
      height: 100vh;
      inset: var(--header-height) 0 0 0;
      backdrop-filter: blur(10px);
      background-color: rgba(0, 0, 0, .25);
      opacity: var(--body-blur-opacity, 0);
      transition: opacity 500ms ease;
      pointer-events: none;
      z-index: -5;
    }
  }
`;

type HeaderProps = {
  mainMenu: LayoutData['leftMenu'] | undefined;
  extraMenu: LayoutData['rightMenu'] | undefined;
} & CurrentLocaleDataType;

const SCROLL_THRESHOLD = 24;

const Header2025 = ({ mainMenu, extraMenu, currentLocale }: HeaderProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(`@media (max-width: ${BREAKPOINTS.XL - 1}px)`);

  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollDirection = useRef<'up' | 'down' | 'none'>('none');

  const primaryNav = useMemo(
    () =>
      mainMenu?.filter(item => item.parentDatabaseId === 0) as
        | Array<PrimaryNavItemType>
        | undefined,
    [mainMenu],
  );

  const extraNav = useMemo(
    () =>
      extraMenu?.map(item => ({
        id: item.id,
        label: item.label ?? '',
        path: item.path ?? '',
        css: !(item.cssClasses && item.cssClasses.length) ? '' : item.cssClasses.join('-'),
      })),
    [extraMenu],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    lastScrollY.current = window.scrollY ?? 0;

    const updateScroll = throttle(() => {
      const currentScrollY = window.scrollY ?? 0;
      const scrollDifference = currentScrollY - lastScrollY.current;

      // Ignore small scrolls
      if (Math.abs(scrollDifference) <= SCROLL_THRESHOLD) {
        lastScrollY.current = currentScrollY;
        return;
      }

      const direction = scrollDifference > 0 ? 'down' : 'up';
      scrollDirection.current = direction;

      if (direction === 'down') {
        setVisible(false);
      } else {
        setVisible(true);
      }

      lastScrollY.current = currentScrollY;
    }, 300);

    window.addEventListener('scroll', updateScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScroll);
    };
  }, []);

  const showHeaderOnFocus = () => {
    setVisible(true);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to re-render according to breakpoint change.
  useEffect(() => {
    if (window !== undefined) {
      const bodyElement = window.document.querySelector('body');

      if (bodyElement) {
        bodyElement.style.setProperty('--header-height', `${headerRef.current?.clientHeight}px`);
      }
    }
  }, [isMobile]);

  return (
    <StyledHeader
      ref={headerRef}
      $visible={visible}
      $scrolling={scrollDirection.current !== 'none'}
      onFocus={showHeaderOnFocus}
    >
      <ContentMaxWidth className="content-wrapper">
        <Link
          href={`/${currentLocale.locale}`}
          aria-label="back to home"
          style={{ marginTop: '8px' }}
        >
          <MynewsdeskLogo width="162" height="28" aria-hidden />
        </Link>

        {!isMobile && (
          <HeaderNavDesktop
            primaryNav={primaryNav}
            extraNav={extraNav}
            currentLocale={currentLocale}
          />
        )}
        {/** Keep it to render the below component for the time of hydration. */}
        <HeaderNavMobile
          primaryNav={primaryNav}
          extraNav={extraNav}
          currentLocale={currentLocale}
        />
      </ContentMaxWidth>
    </StyledHeader>
  );
};

export default Header2025;
